/**
 * Server ↔ client sync for undo material.
 *
 * Two jobs:
 *
 *   hydrate  — on opening an order, pull every step's undo manifest so the Undo
 *              buttons know what they can do *before* the user clicks one. This is
 *              the piece that was missing entirely: the operation list has been
 *              written into checkpoint metadata all along, but nothing ever read
 *              it back, so a second device started blind.
 *   fetch    — download one before-mesh blob on demand.
 *
 * Hydration is gated, not trusting: a manifest is only adopted when the step is
 * marked complete (or is the step being resumed) and its schema version matches.
 * A blob file existing in R2 is **not** evidence that its work is still current —
 * a backward revert discards work locally and the delete call may not have landed,
 * so trusting file presence would resurrect changes the user explicitly threw away.
 */
import useWorkflowStore from '../../../shared/stores/useWorkflowStore';
import { loadBkDesignOrderContext } from '../../../shared/lib/bkDesignOrderContext';
import {
  getBkDesignUndoManifest,
  isUndoApiUnavailable,
} from '../../../shared/services/erpApiService';
import { unpackMeshBlob } from './meshBlob';
import { getBeforeMesh, putBeforeMesh } from './beforeMeshCache';
import { recordTransferSample } from './transferRate';
import useUndoManifestStore, { BEFORE_KIND } from './undoManifestStore';

const LOG_PREFIX = '[UndoManifestSync]';

/** `${orderKey}` → { blobs: Map<`${step}::${tool}`, {url, bytes}>, at } */
const manifestCache = new Map();

let serverUnavailable = false;

function currentOrder() {
  const ctx = loadBkDesignOrderContext();
  if (!ctx?.sales_order || !ctx?.customer) return null;
  return {
    orderKey: `${ctx.sales_order}:${ctx.customer}`,
    salesOrder: ctx.sales_order,
    customer: ctx.customer,
  };
}

function blobIndexKey(stepId, toolId) {
  return `${stepId}::${toolId}`;
}

/**
 * Pull the order's undo manifests and adopt the ones that are still current.
 *
 * Safe to call repeatedly; in-memory manifests always win, so a re-fetch never
 * clobbers live session state.
 *
 * @param {{ force?: boolean }} [opts]
 * @returns {Promise<{ hydrated: number, blobs: number }>}
 */
export async function hydrateUndoManifests({ force = false } = {}) {
  const order = currentOrder();
  if (!order || serverUnavailable) return { hydrated: 0, blobs: 0 };
  if (!force && manifestCache.has(order.orderKey)) {
    const cached = manifestCache.get(order.orderKey);
    return { hydrated: 0, blobs: cached.blobs.size };
  }

  let payload;
  try {
    payload = await getBkDesignUndoManifest({
      salesOrderId: order.salesOrder,
      customer: order.customer,
    });
  } catch (e) {
    if (isUndoApiUnavailable(e)) {
      serverUnavailable = true;
      console.warn(
        `${LOG_PREFIX} undo endpoints not deployed — undo stays device-local this session`,
      );
    } else {
      console.warn(`${LOG_PREFIX} manifest fetch failed`, e);
    }
    return { hydrated: 0, blobs: 0 };
  }

  const blobs = new Map();
  for (const blob of payload?.blobs ?? []) {
    const step = blob?.step;
    const tool = blob?.tool_id;
    const url = blob?.r2_url || blob?.url || blob?.file_url;
    if (!step || !tool || !url) continue;
    blobs.set(blobIndexKey(step, tool), {
      url,
      bytes: Number(blob.bytes) || null,
      fileKey: blob.file_key ?? null,
    });
  }
  manifestCache.set(order.orderKey, { blobs, at: Date.now() });

  const { stepCompletion, currentStep } = useWorkflowStore.getState();
  const store = useUndoManifestStore.getState();
  let hydrated = 0;

  for (const [stepId, manifest] of Object.entries(payload?.steps ?? {})) {
    // Gate on workflow state, never on file existence.
    const isCurrent = stepId === currentStep;
    if (!isCurrent && !stepCompletion?.[stepId]) {
      continue;
    }
    if (store.hydrateStep({ ...manifest, stepId })) hydrated += 1;
  }

  if (hydrated || blobs.size) {
    console.log(
      `${LOG_PREFIX} hydrated ${hydrated} step manifest(s), ${blobs.size} before-mesh(es) available`,
    );
  }
  return { hydrated, blobs: blobs.size };
}

/**
 * Load one before-mesh, local cache first.
 *
 * @param {string} stepId
 * @param {string} toolId
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<ReturnType<typeof unpackMeshBlob>>}
 */
export async function loadBeforeMeshPayload(stepId, toolId, { signal } = {}) {
  const order = currentOrder();
  if (!order) return null;

  const local = await getBeforeMesh(order.orderKey, stepId, toolId);
  if (local) {
    const payload = unpackMeshBlob(local);
    if (payload) return payload;
    console.warn(`${LOG_PREFIX} local blob for ${stepId}/${toolId} did not unpack — refetching`);
  }

  const buffer = await downloadBeforeMesh(order, stepId, toolId, signal);
  if (!buffer) return null;

  const payload = unpackMeshBlob(buffer);
  if (!payload) return null;

  // Keep it for next time; a failed write only costs a repeat download.
  putBeforeMesh(order.orderKey, stepId, toolId, buffer, {
    vertexCount: payload.geometry?.attributes?.position?.count ?? null,
  });
  return payload;
}

async function downloadBeforeMesh(order, stepId, toolId, signal) {
  if (serverUnavailable) return null;

  let entry = manifestCache.get(order.orderKey)?.blobs?.get(blobIndexKey(stepId, toolId));
  if (!entry) {
    await hydrateUndoManifests({ force: true });
    entry = manifestCache.get(order.orderKey)?.blobs?.get(blobIndexKey(stepId, toolId));
  }
  if (!entry?.url) return null;

  try {
    const started = performance.now();
    const res = await fetch(entry.url, { signal });
    if (!res.ok) {
      console.warn(`${LOG_PREFIX} blob GET ${res.status} for ${stepId}/${toolId}`);
      return null;
    }
    const buffer = await res.arrayBuffer();
    // Feeds the router's download-vs-replay estimate with a real measurement.
    recordTransferSample(buffer.byteLength, performance.now() - started);
    return buffer;
  } catch (e) {
    if (e?.name !== 'AbortError') {
      console.warn(`${LOG_PREFIX} blob download failed for ${stepId}/${toolId}`, e);
    }
    return null;
  }
}

/**
 * Synchronous: does the server hold a before-mesh for this (step, tool)?
 * Answered from the hydrated index, so it is only meaningful after
 * {@link hydrateUndoManifests} has run.
 */
export function hasServerBeforeMesh(stepId, toolId) {
  const order = currentOrder();
  if (!order) return false;
  return !!manifestCache.get(order.orderKey)?.blobs?.has(blobIndexKey(stepId, toolId));
}

/** Forget the cached index — call after a revert or a prune changes what exists. */
export function invalidateUndoManifestCache() {
  manifestCache.clear();
}

/** Recorded pointer for a (step, tool) — `blob` vs `prev_step_checkpoint`. */
export function beforeKindFor(stepId, toolId) {
  return useUndoManifestStore.getState().getBeforeRef(stepId, toolId)?.kind ?? null;
}

export { BEFORE_KIND };
